import {useAnalytics} from '@github-ui/use-analytics'
import {clearLocalStorage, useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {useCallback} from 'react'

const GLOBAL_DISMISS_KEY = 'recent-agent-activity-never-show'
const ISSUE_DISMISS_PREFIX = 'recent-agent-activity-dismissed:'

function issueDismissKey(issueId: string): string {
  return `${ISSUE_DISMISS_PREFIX}${issueId}`
}

type UseDismissRecentAgentActivityResult = {
  isDismissed: boolean
  dismissForIssue: () => void
  dismissForever: () => void
}

export function useDismissRecentAgentActivity(issueId: string): UseDismissRecentAgentActivityResult {
  const [issueDismissed, setIssueDismissed] = useLocalStorage(issueDismissKey(issueId), false)
  const [globalDismissed, setGlobalDismissed] = useLocalStorage(GLOBAL_DISMISS_KEY, false)
  const {sendAnalyticsEvent} = useAnalytics()

  const dismissForIssue = useCallback(() => {
    setIssueDismissed(true)
    sendAnalyticsEvent('issues.recent_agent_activity.dismissed', 'RECENT_AGENT_ACTIVITY', {
      scope: 'issue',
      issue_id: issueId,
    })
  }, [setIssueDismissed, sendAnalyticsEvent, issueId])

  const dismissForever = useCallback(() => {
    setGlobalDismissed(true)
    // Clean up any per-issue keys since global dismiss supersedes them
    const keysToRemove = Object.keys(window.localStorage).filter(k => k.startsWith(ISSUE_DISMISS_PREFIX))
    if (keysToRemove.length > 0) {
      clearLocalStorage(keysToRemove)
    }
    sendAnalyticsEvent('issues.recent_agent_activity.dismissed', 'RECENT_AGENT_ACTIVITY', {
      scope: 'global',
    })
  }, [setGlobalDismissed, sendAnalyticsEvent])

  return {
    isDismissed: issueDismissed || globalDismissed,
    dismissForIssue,
    dismissForever,
  }
}
